import React from 'react';
import { useTranslation } from 'react-i18next';

interface ScoreDistributionBarProps {
  criticalCount: number;
  warningCount: number;
  goodCount: number;
  totalCount: number;
  variant?: 'compact' | 'detailed';
}

export const ScoreDistributionBar: React.FC<ScoreDistributionBarProps> = ({
  criticalCount,
  warningCount,
  goodCount,
  totalCount,
  variant = 'compact',
}) => {
  const { t } = useTranslation('optimization');
  const criticalPct = totalCount > 0 ? (criticalCount / totalCount) * 100 : 0;
  const warningPct = totalCount > 0 ? (warningCount / totalCount) * 100 : 0;
  const goodPct = totalCount > 0 ? (goodCount / totalCount) * 100 : 0;

  const barHeight = variant === 'detailed' ? '8px' : '6px';

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center">
        <span className="text-[11px] font-bold tracking-[0.08em] text-primary">{t('distribution.title')}</span>
        <span className="text-xs text-muted-foreground">{t('distribution.products', { count: totalCount })}</span>
      </div>
      <div
        className="w-full rounded-full overflow-hidden flex gap-0.5"
        style={{ height: barHeight, background: 'hsl(var(--muted))' }}
      >
        {criticalPct > 0 && (
          <div data-testid="dist-segment-critical" className="h-full transition-all duration-500" style={{ width: `${criticalPct}%`, background: 'hsl(var(--destructive))' }} />
        )}
        {warningPct > 0 && (
          <div data-testid="dist-segment-warning" className="h-full transition-all duration-500" style={{ width: `${warningPct}%`, background: 'hsl(var(--warning))' }} />
        )}
        {goodPct > 0 && (
          <div data-testid="dist-segment-good" className="h-full transition-all duration-500" style={{ width: `${goodPct}%`, background: 'hsl(var(--success))' }} />
        )}
      </div>
      <div className="flex flex-wrap gap-3 sm:gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-destructive" />
          {t('distribution.critical', { count: criticalCount })}
          {variant === 'detailed' && ` (${Math.round(criticalPct)}%)`}
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full" style={{ background: 'hsl(var(--warning))' }} />
          {t('distribution.warning', { count: warningCount })}
          {variant === 'detailed' && ` (${Math.round(warningPct)}%)`}
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-success" />
          {t('distribution.good', { count: goodCount })}
          {variant === 'detailed' && ` (${Math.round(goodPct)}%)`}
        </span>
      </div>
      {variant === 'detailed' && (
        <p className="text-[11px] text-muted-foreground">{t('distribution.goal')}</p>
      )}
    </div>
  );
};
